"use client";

import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { COUNTRY_PHONE_MAP } from "@/lib/country-codes";

export default function ContactForm() {
  const router = useRouter();
  const phoneCodes = useMemo(() => {
    const codes = Array.from(new Set(Object.values(COUNTRY_PHONE_MAP)));
    codes.sort((a, b) => parseInt(a.slice(1)) - parseInt(b.slice(1)));
    return codes;
  }, []);

  // Clear any leftover "submitted" flag when the form is opened again
  useEffect(() => {
    try {
      sessionStorage.removeItem("mmi_form_submitted");
    } catch {
      /* ignore */
    }
  }, []);

  const [formData, setFormData] = useState({
    firstName: "",
    secondName: "",
    email: "",
    countryCode: "+91",
    mobile: "",
    description: "",
  });

const [errors, setErrors] = useState<Record<string, string>>({});
const [isSubmitting, setIsSubmitting] = useState(false);
const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");

const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  const newErrors: Record<string, string> = {};

  if (!formData.firstName.trim())
    newErrors.firstName = "First name is required";
  if (!formData.email.trim()) newErrors.email = "Email is required";
  if (!formData.mobile.trim()) newErrors.mobile = "Mobile number is required";
  if (!formData.description.trim())
    newErrors.description = "Description is required";

  if (Object.keys(newErrors).length > 0) {
    setErrors(newErrors);
    return;
  }

  setIsSubmitting(true);
  setSubmitStatus("idle");

  try {
    // Local dev has no PHP server, so skip the mailer and show the thank-you page
    const isLocalDev =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "0.0.0.0";

    if (isLocalDev) {
      try {
        sessionStorage.setItem("mmi_form_submitted", "1");
      } catch {
        /* ignore */
      }
      router.push("/thank-you");
      return;
    }

    const res = await fetch("/send-email.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...formData,
        mobile: `${formData.countryCode}${formData.mobile}`,
      }),
    });

    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      throw new Error("Non-JSON response from email endpoint");
    }

    const result = await res.json();

    if (result.success) {
      setFormData({
        firstName: "",
        secondName: "",
        email: "",
        countryCode: "+91",
        mobile: "",
        description: "",
      });
      setErrors({});
      try {
        sessionStorage.setItem("mmi_form_submitted", "1");
      } catch {
        /* ignore */
      }
      router.push("/thank-you");
    } else {
      setSubmitStatus("error");
    }
  } catch {
    setSubmitStatus("error");
  } finally {
    setIsSubmitting(false);
  }
};

  return (
    <motion.form
      initial={{ opacity: 0, x: 20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      onSubmit={handleSubmit}
      className="flex flex-col gap-4 md:gap-5"
    >
      <div className="grid grid-cols-1 gap-4 md:gap-5 md:grid-cols-2">
        <div className="flex flex-col gap-1.5 md:gap-2">
          <label className="text-[13px] md:text-sm font-medium text-black">
            First Name
          </label>
          <input
            type="text"
            placeholder="Enter your first name"
            value={formData.firstName}
            onChange={(e) =>
              setFormData({ ...formData, firstName: e.target.value })
            }
            className="rounded-lg border-2 border-[var(--brand-yellow)] bg-white px-3.5 md:px-4 py-2.5 md:py-3 text-[14px] md:text-base text-black placeholder:text-gray-400 focus:border-[var(--brand-yellow)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-yellow)]/20"
          />
          {errors.firstName && (
            <span className="text-xs text-red-500">{errors.firstName}</span>
          )}
        </div>

        <div className="flex flex-col gap-1.5 md:gap-2">
          <label className="text-[13px] md:text-sm font-medium text-black">
            Second Name
          </label>
          <input
            type="text"
            placeholder="Enter your second name"
            value={formData.secondName}
            onChange={(e) =>
              setFormData({ ...formData, secondName: e.target.value })
            }
            className="rounded-lg border-2 border-[var(--brand-yellow)] bg-white px-3.5 md:px-4 py-2.5 md:py-3 text-[14px] md:text-base text-black placeholder:text-gray-400 focus:border-[var(--brand-yellow)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-yellow)]/20"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:gap-5 md:grid-cols-2">
        <div className="flex flex-col gap-1.5 md:gap-2">
          <label className="text-[13px] md:text-sm font-medium text-black">
            Email Address
          </label>
          <input
            type="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={(e) =>
              setFormData({ ...formData, email: e.target.value })
            }
            className="rounded-lg border-2 border-[var(--brand-yellow)] bg-white px-3.5 md:px-4 py-2.5 md:py-3 text-[14px] md:text-base text-black placeholder:text-gray-400 focus:border-[var(--brand-yellow)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-yellow)]/20"
          />
          {errors.email && (
            <span className="text-xs text-red-500">{errors.email}</span>
          )}
        </div>

        <div className="flex flex-col gap-1.5 md:gap-2">
          <label className="text-[13px] md:text-sm font-medium text-black">
            Mobile Number
          </label>
          <div className="flex items-center gap-0 overflow-hidden rounded-lg border-2 border-[var(--brand-yellow)] bg-white focus-within:ring-2 focus-within:ring-[var(--brand-yellow)]/20">
            <Select
              value={formData.countryCode}
              onValueChange={(val) =>
                setFormData({ ...formData, countryCode: val })
              }
            >
              <SelectTrigger className="w-[84px] md:w-[96px] rounded-none border-0 border-r-2 border-[var(--brand-yellow)] bg-white text-black text-[14px] md:text-base">
                <SelectValue placeholder="+91" />
              </SelectTrigger>
              <SelectContent className="max-h-56 overflow-auto">
                {phoneCodes.map((code) => (
                  <SelectItem key={code} value={code}>
                    {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <input
              type="tel"
              placeholder="Enter your number"
              value={formData.mobile}
              onChange={(e) =>
                setFormData({ ...formData, mobile: e.target.value })
              }
              className="flex-1 border-0 bg-white px-3.5 md:px-4 py-2.5 md:py-3 text-[14px] md:text-base text-black placeholder:text-gray-400 focus:outline-none"
            />
          </div>
          {errors.mobile && (
            <span className="text-xs text-red-500">{errors.mobile}</span>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5 md:gap-2">
        <label className="text-[13px] md:text-sm font-medium text-black">
          Description
        </label>
        <textarea
          placeholder="Enter your description in your detail"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          rows={5}
          className="rounded-lg border-2 border-[var(--brand-yellow)] bg-white px-3.5 md:px-4 py-2.5 md:py-3 text-[14px] md:text-base text-black placeholder:text-gray-400 focus:border-[var(--brand-yellow)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-yellow)]/20"
        />
        {errors.description && (
          <span className="text-xs text-red-500">{errors.description}</span>
        )}
      </div>

<Button
  type="submit"
  disabled={isSubmitting}
  className="w-fit rounded-full bg-[var(--brand-yellow)] px-6 md:px-8 py-2.5 md:py-3 text-[14px] md:text-base font-semibold text-black hover:bg-[var(--brand-yellow)]/90 disabled:opacity-60"
>
  {isSubmitting ? "Sending..." : "Submit"}
</Button>

{submitStatus === "success" && (
  <p className="text-sm text-green-600 font-medium">
    ✓ Message sent successfully! We'll get back to you soon.
  </p>
)}
{submitStatus === "error" && (
  <p className="text-sm text-red-500 font-medium">
    ✗ Something went wrong. Please try again or email us directly.
  </p>
)}
    </motion.form>
  );
}